/**
 * TGC Gear: "Wraithwick" announcement bar.
 *
 * Drives <tgc-announcement-bar> from sections/header-announcements.liquid (Design: Wraithwick).
 *   data-motion="marquee"  every announcement on one line, looping right to left at data-speed px/s
 *   data-motion="slide"    one announcement at a time; the next one slides in every data-interval
 *                          seconds, from the right (data-direction="left") or from below ("up")
 * Pauses on hover, keyboard focus, hidden tabs, the pause button (WCAG 2.2.2) and while a block is
 * selected in the theme editor.
 * With prefers-reduced-motion the marquee stands still (the row can be swiped) and slides swap in place.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const LEAVE_MS = 700;

class TgcAnnouncementBar extends HTMLElement {
  /** @type {HTMLElement[]} */
  #items = [];
  #index = 0;
  #timer = 0;
  #leaveTimer = 0;
  #frame = 0;
  #measured = '';
  /** @type {Set<string>} */
  #pauses = new Set();
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.track = this.querySelector('.announcement-bar__slides');
    if (!this.track) return;

    this.#items = Array.from(this.track.querySelectorAll('.tgc-ann__item')).filter(
      (item) => !item.closest('[data-tgc-clone]')
    );
    if (!this.#items.length) return;

    this.toggle = this.querySelector('[data-tgc-toggle]');
    this.toggle?.addEventListener('click', this.#onToggle);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('visibilitychange', this.#onVisibility);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    reducedMotion.addEventListener?.('change', this.#setup);

    if ('ResizeObserver' in window) {
      this.#observer = new ResizeObserver(this.#schedule);
      this.#observer.observe(this);
    }

    this.#setup();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#onToggle);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    reducedMotion.removeEventListener?.('change', this.#setup);
    this.#observer?.disconnect();
    clearInterval(this.#timer);
    clearTimeout(this.#leaveTimer);
    cancelAnimationFrame(this.#frame);
  }

  #isSlide() {
    return this.dataset.motion === 'slide';
  }

  /** Re-measures after a resize or a font swap, at most once per frame. */
  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (this.#measure() !== this.#measured) this.#setup();
    });
  };

  #measure() {
    const group = this.track.querySelector(':scope > .tgc-ann__group');
    const groupWidth = group ? Math.round(group.getBoundingClientRect().width) : 0;
    return `${Math.round(this.clientWidth)}|${groupWidth}`;
  }

  #setup = () => {
    clearInterval(this.#timer);
    clearTimeout(this.#leaveTimer);
    if (this.#isSlide()) {
      this.#setupSlide();
    } else {
      this.#setupMarquee();
    }
    this.#measured = this.#measure();
  };

  #setupMarquee() {
    const { track } = this;
    let group = track.querySelector(':scope > .tgc-ann__group');

    if (!group) {
      group = document.createElement('div');
      group.className = 'tgc-ann__group';
      this.#items.forEach((item) => group.append(item));
      track.prepend(group);
      this.#observer?.observe(group);
    }

    track.querySelectorAll(':scope > [data-tgc-clone]').forEach((clone) => clone.remove());
    this.removeAttribute('data-ready');

    const groupWidth = group.getBoundingClientRect().width;
    const viewWidth = this.clientWidth;
    if (reducedMotion.matches || !groupWidth || !viewWidth) return;

    // Enough copies that the row never runs out while the first copy scrolls away.
    const copies = Math.ceil(viewWidth / groupWidth);
    for (let i = 0; i < copies; i += 1) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.dataset.tgcClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone
        .querySelectorAll('[data-shopify-editor-block]')
        .forEach((el) => el.removeAttribute('data-shopify-editor-block'));
      track.append(clone);
    }

    const speed = Math.max(5, Number(this.dataset.speed) || 45);
    this.style.setProperty('--tgc-ann-shift', `${groupWidth}px`);
    this.style.setProperty('--tgc-ann-duration', `${(groupWidth / speed).toFixed(2)}s`);
    void track.offsetWidth; // restart the loop from the first announcement
    this.setAttribute('data-ready', '');
  }

  #setupSlide() {
    if (this.#index >= this.#items.length) this.#index = 0;
    this.#items.forEach((item, i) => {
      item.classList.toggle('is-active', i === this.#index);
      item.classList.remove('is-leaving');
    });
    this.setAttribute('data-ready', '');
    this.#start();
  }

  #start() {
    clearInterval(this.#timer);
    if (!this.#isSlide() || this.#pauses.size || this.#items.length < 2) return;
    const seconds = Math.min(60, Math.max(2, Number(this.dataset.interval) || 4));
    this.#timer = window.setInterval(() => this.#go(this.#index + 1), seconds * 1000);
  }

  /**
   * @param {number} to
   * @param {boolean} [instant]
   */
  #go(to, instant = false) {
    const count = this.#items.length;
    if (count < 2) return;
    const next = ((to % count) + count) % count;
    if (next === this.#index) return;

    const leaving = this.#items[this.#index];
    clearTimeout(this.#leaveTimer);
    this.#items.forEach((item) => item.classList.remove('is-leaving'));
    leaving.classList.remove('is-active');

    if (!instant && !reducedMotion.matches) {
      leaving.classList.add('is-leaving');
      this.#leaveTimer = window.setTimeout(() => leaving.classList.remove('is-leaving'), LEAVE_MS);
    }

    this.#items[next].classList.add('is-active');
    this.#index = next;
  }

  /** @param {EventTarget | null} target */
  #show(target) {
    if (!this.#isSlide() || !(target instanceof Element)) return;
    const index = this.#items.findIndex((item) => item.contains(target));
    if (index > -1) this.#go(index, true);
  }

  /** @param {string} reason */
  #pause(reason) {
    this.#pauses.add(reason);
    this.setAttribute('data-paused', '');
    clearInterval(this.#timer);
  }

  /** @param {string} reason */
  #resume(reason) {
    this.#pauses.delete(reason);
    if (this.#pauses.size) return;
    this.removeAttribute('data-paused');
    this.#start();
  }

  /** Pause button: aria-pressed="true" while the shopper has paused the announcements. */
  #onToggle = () => {
    const paused = this.toggle.getAttribute('aria-pressed') !== 'true';
    this.toggle.setAttribute('aria-pressed', String(paused));
    if (paused) {
      this.#pause('user');
    } else {
      this.#resume('user');
    }
  };

  #onPointerEnter = () => this.#pause('hover');

  #onPointerLeave = () => this.#resume('hover');

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    this.#pause('focus');
    this.#show(event.target);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#resume('focus');
  };

  #onVisibility = () => (document.hidden ? this.#pause('hidden') : this.#resume('hidden'));

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#pause('editor');
    this.#show(event.target);
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    this.#resume('editor');
  };
}

if (!customElements.get('tgc-announcement-bar')) {
  customElements.define('tgc-announcement-bar', TgcAnnouncementBar);
}
